import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, BarList, LineChart } from "@tremor/react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface CompanyAnalyticsData {
  total_users: number;
  total_customers: number;
  total_estimates: number;
}

interface AdminAnalyticsProps {
  analyticsData: Partial<CompanyAnalyticsData>;
}

const AdminAnalytics: React.FC<AdminAnalyticsProps> = ({ analyticsData }) => {
  const [companyActivity, setCompanyActivity] = useState<any[]>([]);
  const [departmentStats, setDepartmentStats] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { profile } = useAuth();
  
  // Define chart colors using Tremor color tokens
  const chartColors = ["blue", "emerald"];

  useEffect(() => {
    const fetchCompanyAnalytics = async () => {
      try {
        setIsLoading(true);
        
        if (!profile?.company_id) {
          setIsLoading(false);
          return;
        }
        
        // Fetch real estimates data grouped by month (last 6 months)
        const startDate = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000);
        const endDate = new Date();
        console.log('[Analytics] Query window:', { 
          start: startDate.toISOString(), 
          end: endDate.toISOString(),
          company_id: profile.company_id 
        });
        
        const { data: estimatesData, error: estimatesError } = await supabase
          .from('estimates')
          .select('created_at')
          .eq('company_id', profile.company_id)
          .gte('created_at', startDate.toISOString());

        if (estimatesError) {
          console.error('Error fetching estimates:', estimatesError);
        }

        // Fetch real customers data grouped by month (last 6 months)
        // Note: Company filtering handled by RLS via created_by relationship
        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('created_at')
          .gte('created_at', startDate.toISOString());
        
        console.log('[Analytics] Raw query results:', {
          estimatesCount: estimatesData?.length || 0,
          customersCount: customersData?.length || 0
        });

        if (customersError) {
          console.error('Error fetching customers:', customersError);
        }

        // Group data by month
        const monthlyData: Record<string, { estimates: number; customers: number }> = {};
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        // Initialize last 6 months
        for (let i = 5; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const monthKey = `${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;
          monthlyData[monthKey] = { estimates: 0, customers: 0 };
        }

        // Count estimates by month
        estimatesData?.forEach((estimate) => {
          const date = new Date(estimate.created_at);
          const monthKey = `${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;
          if (monthlyData[monthKey]) {
            monthlyData[monthKey].estimates++;
          }
        });

        // Count customers by month
        customersData?.forEach((customer) => {
          const date = new Date(customer.created_at);
          const monthKey = `${months[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;
          if (monthlyData[monthKey]) {
            monthlyData[monthKey].customers++;
          }
        });

        const activityData = Object.entries(monthlyData).map(([month, data]) => ({
          month,
          estimates: data.estimates,
          customers: data.customers
        }));

        console.log('[Analytics] Activity data:', activityData);
        console.log('[Analytics] Activity sample (first 2):', activityData.slice(0, 2));
        console.log('[Analytics] Chart colors:', chartColors);

        // Fetch actual team members for department stats
        console.log('[Analytics] Fetching company_members with company_id:', profile.company_id);
        
        // First, get ALL members to check status distribution
        const { data: allMembersData } = await supabase
          .from('company_members')
          .select('role, user_id, status')
          .eq('company_id', profile.company_id);
        
        // Log status distribution
        const statusCounts = allMembersData?.reduce((acc: any, member) => {
          const status = member.status || 'unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {}) || {};
        
        console.log('[Analytics] ALL company_members stats:', {
          total: allMembersData?.length || 0,
          statusDistribution: statusCounts,
          sampleRows: allMembersData?.slice(0, 5),
          company_id: profile.company_id
        });
        
        // Now fetch only active members
        const { data: membersData, error: membersError } = await supabase
          .from('company_members')
          .select('role, user_id, status')
          .eq('company_id', profile.company_id)
          .eq('status', 'active');

        console.log('[Analytics] ACTIVE company_members result:', {
          activeCount: membersData?.length || 0,
          error: membersError,
          sampleRows: membersData?.slice(0, 5)
        });

        if (membersError) {
          console.error('[Analytics] Error fetching team members:', membersError);
        }

        // Generate department statistics based on actual data
        const roleStats = membersData?.reduce((acc: any, member) => {
          const role = member.role || 'member';
          if (!acc[role]) {
            acc[role] = { count: 0, userIds: [] };
          }
          acc[role].count++;
          acc[role].userIds.push(member.user_id);
          return acc;
        }, {}) || {};

        const departmentData = Object.entries(roleStats).map(([role, data]: [string, any]) => ({
          name: role.charAt(0).toUpperCase() + role.slice(1),
          value: data.count
        })).sort((a, b) => b.value - a.value);

        console.log('[Analytics] Role stats breakdown:', roleStats);
        console.log('[Analytics] Final departmentStats for BarList:', {
          count: departmentData.length,
          data: departmentData,
          top5Roles: departmentData.slice(0, 5)
        });

        setCompanyActivity(activityData);
        setDepartmentStats(departmentData);
      } catch (error) {
        console.error('Error fetching company analytics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyAnalytics();
  }, [profile?.company_id]);

  return (
    <div className="grid gap-6">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Company Activity</CardTitle>
            <CardDescription>Estimates and customers over the last 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <BarChart
                data={companyActivity}
                index="month"
                categories={["estimates", "customers"]}
                colors={chartColors}
                valueFormatter={(number: number) => `${number}`}
                className="h-64"
                stack={false}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Team Distribution</CardTitle>
            <CardDescription>Team members by role/department</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : departmentStats.length > 0 ? (
              <BarList
                data={departmentStats}
                className="mt-4"
              />
            ) : (
              <p className="text-muted-foreground text-center py-8">No team data available</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Growth Trends</CardTitle>
            <CardDescription>Company performance trends</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <LineChart
                data={companyActivity}
                index="month"
                categories={["estimates", "customers"]}
                colors={chartColors}
                valueFormatter={(number: number) => `${number}`}
                className="h-64"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
